import { AnomalyStatusPill } from '@/features/quality/components/AnomalyStatusPill';
import { metricLabel } from '@/features/quality/labels';
import type { QualityAnomaly } from '@/features/quality/types';

interface Props {
  anomalies: QualityAnomaly[];
  onAcknowledge: (id: string) => void;
  onResolve: (id: string) => void;
  busyId: string | null;
}

export function AnomalyList({ anomalies, onAcknowledge, onResolve, busyId }: Props): JSX.Element {
  if (anomalies.length === 0) {
    return <p className="text-sm text-slate-500 dark:text-ink-400">Nothing flagged here — you&rsquo;re all clear.</p>;
  }

  return (
    <ul className="divide-y divide-slate-100 dark:divide-white/10">
      {anomalies.map((anomaly) => {
        const [low, high] = anomaly.expected_range;
        const isBusy = busyId === anomaly.id;
        return (
          <li key={anomaly.id} className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700 dark:text-ink-200">
                  {metricLabel(anomaly.metric)}
                </span>
                <AnomalyStatusPill status={anomaly.status} />
                <span className="text-xs text-slate-400 dark:text-ink-500">
                  {Math.round(anomaly.confidence * 100)}% sure
                </span>
              </div>
              <p className="mt-1 truncate text-xs text-slate-400 dark:text-ink-500">in {anomaly.asset_fqn}</p>
              <p className="mt-1 text-sm text-slate-800 dark:text-ink-100">
                now <span className="font-medium">{anomaly.value}</span>
                {low !== null && high !== null && (
                  <span className="text-slate-500 dark:text-ink-400">
                    {' '}
                    · usually between {low} and {high}
                  </span>
                )}
                <span className="text-slate-400 dark:text-ink-500"> · {new Date(anomaly.ts).toLocaleString()}</span>
              </p>
              {anomaly.root_cause && (
                <p className="mt-0.5 text-sm text-slate-600 dark:text-ink-200">{anomaly.root_cause}</p>
              )}
              {anomaly.suggested_action && (
                <p className="mt-0.5 text-xs text-slate-500 dark:text-ink-400">→ {anomaly.suggested_action}</p>
              )}
            </div>

            {anomaly.status !== 'resolved' && (
              <div className="flex shrink-0 gap-2">
                {anomaly.status === 'open' && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => onAcknowledge(anomaly.id)}
                    className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50 disabled:opacity-50 dark:text-ink-200 dark:ring-white/15 dark:hover:bg-white/5"
                  >
                    Mark as seen
                  </button>
                )}
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => onResolve(anomaly.id)}
                  className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  Mark as fixed
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
